import { theme } from 'antd';
import { bookingType } from 'config/bookingType';
import dayjs from 'dayjs';
import dateFunctions from 'library/helpers/dateFunctions';
import { useManagePermittedRoles } from 'library/hooks';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectBookingDateConfigByOrg, selectBookingsByDeviceID } from 'reduxStore/bookings/selectors';
import { selectPeople } from 'reduxStore/people/selectors';
import { bookingActions } from 'reduxStore/bookings/slice';
import { selectLockersDevices } from 'reduxStore/devices/selectors';
import { selectSettingsRoomConfiguration } from 'reduxStore/organisations/selectors';
import { selectPeopleByPersonID } from 'reduxStore/people/selectors';
import { Alert, Button, DatePicker, Flex, Form, Input, Modal, Select, Tooltip, Typography } from 'zui/Antd';
const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const CreateBookingModal = ({ children, visible: visibleProp, setVisible: setVisibleProp, deviceID, personID }) => {
	// Hooks
	const {
		token: { paddingLG, paddingXL, margin, marginXS, paddingXS, paddingXXL = 48 }
	} = theme.useToken();
	const [form] = Form.useForm();
	const dispatch = useDispatch();

	//Watch
	const selectedDeviceID = Form.useWatch('deviceID', form);

	// Redux state
	const people = useSelector(selectPeople);
	const person = useSelector((state) => selectPeopleByPersonID(state, personID));

	const devices = useSelector(selectLockersDevices);
	const { loading: loadingPeople } = useSelector((state) => state.People);
	const { createBookingLoading } = useSelector((state) => state.Bookings);

	const currentBookings = useSelector((state) => selectBookingsByDeviceID(state, selectedDeviceID));
	const bookingConfigByCategory = useSelector(selectBookingDateConfigByOrg);
	const roomConfiguration = useSelector((state) => selectSettingsRoomConfiguration(state));

	// ✅ Local state
	const [internalVisible, setInternalVisible] = useState(false);
	const visible = visibleProp ?? internalVisible;
	const setVisible = setVisibleProp ?? setInternalVisible;

	// Local state
	const [showAlert, setShowAlert] = useState(false);
	const [isUsingDefaultSchedule, setIsUsingDefaultSchedule] = useState(false);
	const schedule = Form.useWatch('schedule', form);

	// ✅ Permissions
	const canAddBooking = useManagePermittedRoles('addBooking');
	const permissionDeniedMessage = "You don't have permission to perform this action.";

	// Memoized selected device based on selectedDeviceID
	const selectedDevice = useMemo(() => {
		return devices.find((d) => d.deviceID === selectedDeviceID) || null;
	}, [devices, selectedDeviceID]);

	// Check if the selected device's category has a valid configuration
	const isDefaultSchedule = useMemo(() => {
		if (!selectedDevice || !roomConfiguration?.categories?.length) return false;

		const matchedCategory = roomConfiguration.categories.find(
			(category) => category.ID === String(selectedDevice.categoryID)
		);

		return !!(matchedCategory?.minBookingDate && matchedCategory?.maxBookingDate);
	}, [roomConfiguration, selectedDevice]);

	useEffect(() => {
		setIsUsingDefaultSchedule(isDefaultSchedule);
	}, [isDefaultSchedule]);

	// Memoized category config based on device
	const categoryConfig = useMemo(() => {
		if (!selectedDevice) return null;
		const categoryID = selectedDevice.categoryID;
		return bookingConfigByCategory?.[categoryID] || null;
	}, [selectedDevice, bookingConfigByCategory]);

	// Set schedule to default range if room configuration setting is available
	// Set up same than Locker_App
	useEffect(() => {
		// Default Min/Max if there is not range set up
		let min = dateFunctions['today']();
		let max = dateFunctions['twelveMonthsFromToday']();

		// IF category config exist, use it to set min and max
		if (categoryConfig) {
			max = categoryConfig?.maxBookingValueDate;
			min = categoryConfig?.minBookingValueDate;
		}

		form.setFieldsValue({
			schedule: [min, max]
		});
	}, [visible, categoryConfig, form, selectedDeviceID]);

	// Set initial values for deviceID and name when modal opens
	useEffect(() => {
		if (visible && deviceID) {
			form.setFieldsValue({
				deviceID,
				name: 'Booking for ' + (selectedDevice?.name || 'Locker')
			});
		}
	}, [deviceID, visible, form, selectedDevice]);

	// Set initial values for personID if provided
	useEffect(() => {
		if (personID) {
			form.setFieldsValue({
				personID
			});
		}
	}, [form, personID, person, visible]);

	// Check for overlapping bookings
	useEffect(() => {
		if (!Array.isArray(schedule) || schedule.length !== 2 || !currentBookings) {
			setShowAlert(false);
			return;
		}

		const [newStart, newEnd] = schedule.map((s) => dayjs(s));

		const hasOverlap = currentBookings.some((booking) => {
			const existingStart = dayjs(booking.startTime);
			const existingEnd = dayjs(booking.endTime);

			// Check if the new time range overlaps with an existing one
			return newStart.isBefore(existingEnd) && newEnd.isAfter(existingStart);
		});

		setShowAlert(hasOverlap);
	}, [schedule, currentBookings]);

	const handleCancel = () => {
		setVisible(false);
		form.resetFields();
		setShowAlert(false);
	};

	const handleCreateBooking = (values) => {
		let startDate = values.schedule[0];
		const endDate = values.schedule[1];

		// Adjust start only if needed
		startDate = dateFunctions.adjustStartIfPast(startDate);

		dispatch(
			bookingActions.createBooking({
				data: {
					name: values.name,
					deviceID: values.deviceID,
					startTime: startDate,
					endTime: endDate,
					assignedToPerson: values.personID,
					bookingTypeID: bookingType.LOCKER
				}
			})
		);

		handleCancel();
	};

	return (
		<>
			<Tooltip title={!canAddBooking ? permissionDeniedMessage : 'Create a new booking'} placement='top'>
				<span onClick={(e) => canAddBooking && setVisible(true)}>
					{children ?? (
						<Button disabled={!canAddBooking || createBookingLoading} type='primary'>
							Create Booking
						</Button>
					)}
				</span>
			</Tooltip>

			{/* The Modal */}
			<Modal
				open={visible}
				onCancel={handleCancel}
				centered
				closeIcon={false}
				width={716}
				destroyOnClose={true}
				styles={{
					content: {
						padding: `${paddingLG}px ${paddingXL}px`
					}
				}}
				footer={[
					<Button onClick={handleCancel} type='default'>
						Cancel
					</Button>,
					<Button form={form} key='submit' htmlType='submit' type='primary' loading={createBookingLoading}>
						Book
					</Button>
				]}
			>
				<Flex vertical gap={paddingXL}>
					<Flex align='center' gap={4}>
						<Title level={3} style={{ margin: 0 }}>
							Create Booking
						</Title>
					</Flex>

					<Form
						id={form}
						form={form}
						layout='vertical'
						onFinish={handleCreateBooking}
						style={{ marginBottom: paddingXXL }}
						// you could also do initialValues here, but it won't pick up prop changes after mount
					>
						<Flex vertical gap={paddingXL}>
							<Form.Item
								label={<Text strong>Booking Name</Text>}
								name='name'
								rules={[{ required: true, message: 'Please enter a booking name' }]}
								style={{ width: '100%', margin: 0 }}
							>
								<Input placeholder='e.g. Booking 236' />
							</Form.Item>

							<Flex justify='space-between' gap={margin}>
								<Form.Item
									label={<Text strong>Person</Text>}
									name='personID'
									rules={[{ required: true, message: 'Please select a person' }]}
									style={{ width: '100%', margin: 0 }}
								>
									<Select
										placeholder='Select Person'
										disabled={loadingPeople}
										showSearch
										optionFilterProp='label'
										options={people?.map((person) => ({
											value: person.personID,
											label:
												person.firstName && person.lastName
													? `${person.firstName} ${person.lastName}`
													: person.displayName || person.email
										}))}
									/>
								</Form.Item>

								<Form.Item
									label={<Text strong>Locker</Text>}
									name='deviceID'
									rules={[{ required: true, message: 'Please select a locker' }]}
									style={{ width: '100%', margin: 0 }}
								>
									<Select
										placeholder='Select Locker'
										showSearch
										optionFilterProp='label'
										options={devices.map((device) => ({
											value: device.deviceID,
											label: device.name
										}))}
									/>
								</Form.Item>
							</Flex>

							<Form.Item
								label={<Text strong>Schedule</Text>}
								name='schedule'
								rules={[{ required: true, message: 'Please select a schedule' }]}
								style={{ margin: 0, width: '100%' }}
								extra={
									isUsingDefaultSchedule && (
										<Alert
											showIcon
											type='info'
											message='Schedule based on default settings. You can adjust it if needed.'
											closable
										/>
									)
								}
							>
								<RangePicker
									style={{ marginBottom: marginXS, width: '100%' }}
									placeholder={['Start Date', 'End Date']}
									minDate={dayjs()}
									format='DD/MM/YYYY hh:mm A'
									onChange={(dates) => {
										const [rawStart, rawEnd] = dates || [];

										let startOut = null;
										let endOut = null;

										if (rawStart) {
											startOut = categoryConfig?.minBookingDate
												? dateFunctions.applyRuleTime(
														rawStart,
														categoryConfig.minBookingDate,
														false
													)
												: dateFunctions.today();
										}

										if (rawEnd) {
											endOut = categoryConfig?.maxBookingDate
												? dateFunctions.applyRuleTime(
														rawEnd,
														categoryConfig.maxBookingDate,
														true
													)
												: dateFunctions.twelveMonthsFromToday();
										}

										form.setFieldsValue({ schedule: [startOut, endOut] });
									}}
								/>
							</Form.Item>
						</Flex>
						{showAlert && (
							<Alert
								style={{ marginTop: paddingXS }}
								message='This locker already has an existing booking during the selected time. proceeding may result in a conflict.'
							/>
						)}
					</Form>
				</Flex>
			</Modal>
		</>
	);
};

export default CreateBookingModal;
